import React, { Component } from 'react';
import Icon from 'components/icon/index';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import reduxUtils from 'util/redux';
import classnames from 'classnames';

import AnimatedCounter from 'components/animated-counter';
import { t, Trans } from 'translations';
import Tooltip from 'components/tooltip';
import stopPropagation from 'util/dom/stop-propagation';
import { MAX_BAGS, MAX_ITEMS } from 'constants/cart';

class CartCounter extends Component {
    static propTypes = {
        id: PropTypes.string,
        productName: PropTypes.string,
        amount: PropTypes.number.isRequired,
        totalBags: PropTypes.number.isRequired,
        add: PropTypes.func.isRequired,
        tabIndex: PropTypes.number.isRequired,
        fetchingId: PropTypes.string,
        remove: PropTypes.func.isRequired,
        setTooltipId: PropTypes.func.isRequired,
        facet: PropTypes.string,
    };

    static defaultProps = {
        tabIndex: 0,
    };

    static isEmpty(props) {
        return props.amount <= 0;
    }

    static isFull(props) {
        const { amount, totalBags } = props;
        return amount >= MAX_ITEMS || totalBags >= MAX_BAGS;
    }

    constructor(props) {
        super(props);

        this.state = {
            isEmpty: CartCounter.isEmpty(props),
            isFull: CartCounter.isFull(props),
        };
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.amount !== this.props.amount) {
            this.runChecks(nextProps);
        }
    }

    shouldComponentUpdate({ fetchingId, amount, id }) {
        return fetchingId === id || amount !== this.props.amount;
    }

    onAdd = () => {
        const { add } = this.props;
        const { isFull } = this.state;
        const { isBusy } = this;
        return !isFull && !isBusy() && add();
    };

    runChecks = (props) => {
        this.setState({
            isEmpty: CartCounter.isEmpty(props),
            isFull: CartCounter.isFull(props),
        });
    };

    hideTooltip = () => {
        const { id, setTooltipId } = this.props;

        if (id) {
            setTooltipId({ tooltipId: id });
        }
    };

    showTooltip = () => {
        const { id, setTooltipId } = this.props;

        if (id) {
            setTooltipId({ tooltipId: id });
        }
    };

    triggerRemove = e => {
        const { remove } = this.props;

        if (this.enableTooltip) {
            return; // Let the tooltip be triggered
        }

        stopPropagation(e);
        remove();
    };

    isBusy = () => {
        return this.props.fetchingId === this.props.id;
    };

    get actions() {
        return [
            {
                name: <Trans path={'tooltip.cancel'}/>,
            },
            {
                name: <Trans path={'tooltip.confirm'}/>,
                isHighlighted: true,
                event: () => {
                    this.props.remove();
                },
            },
        ];
    }

    get enableTooltip() {
        const { amount, facet } = this.props;
        return amount === 1 && facet === 'cart';
    }

    renderRemoveButton() {
        const { productName, tabIndex } = this.props;
        const { isEmpty } = this.state;
        const { isBusy, triggerRemove } = this;
        return (
            <button className="cart-counter--remove u-reset"
                tabIndex={tabIndex}
                onClick={e => !isEmpty && !isBusy() && triggerRemove(e)}
                aria-label={t('accessibility.label.remove_one', {
                    product_name: productName,
                })}>
                <Icon symbol="basket-remove" size="large"/>
            </button>
        );
    }

    render() {
        const { amount, facet, productName, tabIndex } = this.props;
        const { isEmpty, isFull } = this.state;
        const { runChecks, showTooltip, hideTooltip, enableTooltip } = this;
        /**
         * cart-counter is clickable and increments the amount
         * cart-counter--content is not clickable
         * cart-counter--add is clickable and increments the amount
         * cart-counter--remove is clickable and decrements the amount
         */
        return (
            <div
                tabIndex={tabIndex}
                aria-label={t('accessibility.label.add_to_cart', {
                    product_name: productName,
                })}
                role="button"
                className={classnames(
                    'cart-counter',
                    { [`cart-counter__${ facet }`]: facet },
                    { 'cart-counter__is-empty': isEmpty },
                    { 'cart-counter__is-filled': !isEmpty },
                    { 'cart-counter__is-full': isFull },
                )}
                onClick={this.onAdd}
            >
                <div className="cart-counter--content" onClick={e => e.stopPropagation()}>
                    <button tabIndex={tabIndex} className="cart-counter--add u-reset" onClick={this.onAdd}
                        aria-label={t('accessibility.label.add_to_cart', {
                            product_name: productName,
                        })}>
                        <Icon symbol="basket-add" size="large"/>
                    </button>
                    <AnimatedCounter onFinish={() => runChecks(this.props)}
                        ariaLabel={t('accessibility.label.product_count')}
                        className="cart-counter--products-amount"
                        amount={amount}/>
                    {facet === 'cart' ?
                        <Tooltip
                            interactionType={enableTooltip ? 'click' : 'none'}
                            content={<Trans path={'cart.remove_question'}/>}
                            facet={['base', 'block']}
                            actions={this.actions}
                            forcePosition={'left'}
                            onShow={showTooltip}
                            onHide={hideTooltip}
                        >{this.renderRemoveButton()}</Tooltip> : this.renderRemoveButton()}
                </div>
            </div>
        );
    }
}

export default connect(
    reduxUtils.mapStateToProps({
        fetchingId: 'cart.fetchingId',
        cartErrorId: 'cart.productError.fetchingId',
        totalBags: 'cart.bagInfo.total_bags',
    }),
    reduxUtils.mapDispatchToProps({
        setTooltipId: 'cart.setTooltipId',
    }),
)(CartCounter);



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/cart-counter/index.js